import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { 
  LayoutDashboard, Package, FolderOpen, Settings, LogOut, 
  Menu, ChefHat, Store, BarChart3, History, ShoppingCart, Tag
} from 'lucide-react';
import { useState } from 'react';

export default function AdminLayout({ children, establishmentName }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/pedidos', label: 'Pedidos', icon: ShoppingCart },
    { path: '/admin/products', label: 'Produtos', icon: Package },
    { path: '/admin/categories', label: 'Categorias', icon: FolderOpen },
    { path: '/admin/promocoes', label: 'Promoções', icon: Tag },
    { path: '/admin/history', label: 'Histórico', icon: History },
    { path: '/admin/reports', label: 'Relatórios', icon: BarChart3 },
    { path: '/admin/settings', label: 'Configurações', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="admin-wrapper">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="admin-sidebar">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Delivery OS</h1>
              <p className="text-xs text-slate-400">Painel Admin</p>
            </div>
          </div>
        </div>

        {/* Current Establishment Display */}
        {establishmentName && (
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg">
              <Store className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-white truncate" data-testid="current-establishment-name">
                {establishmentName}
              </span>
            </div>
          </div>
        )}

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map(item => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-red-500 text-white'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="px-4 py-2 mb-2">
            <p className="text-xs text-slate-500">Logado como</p>
            <p className="text-sm text-slate-300 truncate">{user?.name}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-6 -mt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <div className="flex items-center gap-2">
            <ChefHat className="w-6 h-6 text-red-500" />
            <span className="font-bold">Delivery OS</span>
          </div>
          <div className="w-10" />
        </div>

        {children}
      </main>
    </div>
  );
}
